import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Grid,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import { Formik, Form } from 'formik';
import { useDispatch, useSelector } from 'react-redux';

import { formInitialValuesCreate } from '../formik/create/formModels/formInitialValues';
import { validationSchemaCreate } from '../formik/create/formModels/validationSchema';
import { formFieldModelCreate } from '../formik/create/formModels/ausenciaEmpleadoFormModel';
import InputField from '../../../../components/formFields/InputField';
import SelectField from '../../../../components/formFields/selectField';
import AutocompleteField from '../../../../components/formFields/autocompleteField';
import DatePickerField from '../../../../components/formFields/datePickerField';
import { saveEmployeeAbsenceStore } from '../../../../store/employee_absences/thunks';
import { useResponseHandler } from '../../../../hooks/useResponseHandler';
import { useTableState } from '../../../../hooks/useTableState';
import { absenceTypes, approvalStatuses } from '../data/data';

const ABSENCE_TYPES = absenceTypes;

interface Props {
  open: boolean;
  setOpen: (open: boolean) => void;
  onSuccess: () => void;
}

export const CreateAusenciaEmpleadoModal = ({ open, setOpen, onSuccess }: Props) => {
  const dispatch = useDispatch<any>();
  const { handleResponse, ResponseComponents } = useResponseHandler();
  const { pagePagination, rowsPerPage, searchTerm } = useTableState('employeeAbsences');

  // Select options coming from the table fetch
  const employeesData =
    useSelector((state: any) => state.employeeAbsences.employeeAbsences_employees_select) || [];

  const {
    formId,
    formField: { empleado_id, absence_type, start_date, end_date, description, approval_status },
  } = formFieldModelCreate;

  const handleClose = () => {
    setOpen(false);
  };

  const _handleSubmit = async (values: any, actions: any) => {
    const payload = {
      empleado_id: values[empleado_id.name]?.value,
      absence_type: values[absence_type.name]?.value,
      start_date: values[start_date.name],
      end_date: values[end_date.name],
      description: values[description.name],
      approval_status: values[approval_status.name]?.value,
    };

    const response = await dispatch(
      saveEmployeeAbsenceStore(payload, pagePagination, rowsPerPage, searchTerm, 1)
    );
    await handleResponse(response, 'registrar ausencia', true);

    if (response?.ok) {
      handleClose();
      onSuccess();
    }
    actions.setSubmitting(false);
  };

  return (
    <>
      {ResponseComponents}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow:
              '0px 12px 24px -4px rgba(145, 158, 171, 0.12), 0px 0px 2px 0px rgba(145, 158, 171, 0.2)',
          },
        }}
      >
        <DialogTitle sx={{ m: 0, p: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              p: 1,
              borderRadius: 2,
              bgcolor: 'rgba(102, 60, 193, 0.08)',
              display: 'flex',
              color: '#663cc1',
            }}
          >
            <AddIcon />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Registrar Ausencia
            </Typography>
            <Typography variant="body2" sx={{ color: '#32475C99' }}>
              Completa los campos para registrar una ausencia (vacaciones, incapacidades, etc).
            </Typography>
          </Box>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 16,
              top: 24,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <Formik
          initialValues={formInitialValuesCreate}
          validationSchema={validationSchemaCreate[0]}
          onSubmit={_handleSubmit}
        >
          {({ isSubmitting, isValid, dirty }) => (
            <Form id={formId}>
              <DialogContent
                dividers
                sx={{ borderTop: '1px solid #32475C1F', borderBottom: '1px solid #32475C1F', p: 3 }}
              >
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12 }}>
                    <AutocompleteField
                      name={empleado_id.name}
                      label={empleado_id.label}
                      options={employeesData}
                      fullWidth
                    />
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <AutocompleteField
                      name={absence_type.name}
                      label={absence_type.label}
                      options={ABSENCE_TYPES}
                      fullWidth
                    />
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <AutocompleteField
                      name={approval_status.name}
                      label={approval_status.label}
                      options={approvalStatuses}
                      fullWidth
                    />
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <DatePickerField name={start_date.name} label={start_date.label} />
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <DatePickerField name={end_date.name} label={end_date.label} />
                  </Grid>

                  <Grid size={{ xs: 12 }}>
                    <InputField
                      name={description.name}
                      label={description.label}
                      multiline
                      rows={3}
                      fullWidth
                    />
                  </Grid>
                </Grid>

                <Box
                  sx={{
                    mt: 3,
                    p: 2,
                    borderRadius: 2,
                    bgcolor: 'rgba(105, 108, 255, 0.08)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <InfoOutlinedIcon sx={{ color: '#696CFF' }} fontSize="small" />
                  <Typography variant="body2" sx={{ color: '#696CFF' }}>
                    Las ausencias registradas por RRHH son automáticamente aprobadas y afectan el
                    calendario general de los colaboradores.
                  </Typography>
                </Box>
              </DialogContent>
              <DialogActions sx={{ p: 3 }}>
                <Button
                  onClick={handleClose}
                  variant="text"
                  color="inherit"
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting || !(isValid && dirty)}
                  startIcon={<SaveIcon />}
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                >
                  Registrar
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </>
  );
};
